import { MapPin } from 'lucide-react'
import type { GeolocationStatus, CityInfo } from '../hooks/useGeolocation'
import { useI18n } from '../i18n/useI18n'

interface Props {
  status: GeolocationStatus
  cityInfo: CityInfo | null
  matchCount: number
  cityFiltered: boolean
  areaExpanded: boolean
  onRequestLocation: () => void
  onShowAllAreas: () => void
}

/**
 * 店舗一覧上部に現在地情報と絞り込み状態を表示するバナー。
 * - granted + cityInfo あり: 「📍 {prefecture}{city} の店舗 N 件」+ 全エリア表示リンク
 * - areaExpanded: フォールバック表示（市区町村 0 件 → 都道府県全件 or 全国）
 * - denied/unavailable: 「近くの店舗を探す」ボタン
 * - idle/loading: 非表示
 */
export default function LocationBanner({
  status,
  cityInfo,
  matchCount,
  cityFiltered,
  areaExpanded,
  onRequestLocation,
  onShowAllAreas,
}: Props) {
  const { t } = useI18n()

  if (status === 'idle' || status === 'loading') return null

  if (status === 'denied' || status === 'unavailable' || !cityInfo) {
    return (
      <div className="mx-4 mt-3 rounded-xl border border-gray-200 bg-white p-3 flex items-center justify-between gap-3">
        <p className="font-body text-xs text-text-secondary flex-1">
          {status === 'denied' ? t('stores.locationDenied') : t('stores.enableLocation')}
        </p>
        <button
          type="button"
          onClick={onRequestLocation}
          className="tap-effect shrink-0 rounded-full bg-navy px-3 py-1.5 font-body text-xs font-medium text-white"
        >
          <MapPin size={12} className="mr-1 inline" />
          {t('stores.enableLocationButton')}
        </button>
      </div>
    )
  }

  // granted + cityInfo あり
  return (
    <div className="mx-4 mt-3 rounded-xl bg-navy/5 p-3">
      <div className="flex items-center gap-2">
        <MapPin size={14} className="text-navy shrink-0" />
        <p className="font-body text-xs text-text-primary flex-1">
          {areaExpanded
            ? t('stores.areaExpanded', { prefecture: cityInfo.prefecture, count: matchCount })
            : t('stores.currentArea', {
                prefecture: cityInfo.prefecture,
                city: cityInfo.city,
                count: matchCount,
              })}
        </p>
        {cityFiltered && (
          <button
            type="button"
            onClick={onShowAllAreas}
            className="tap-effect shrink-0 font-body text-xs font-medium text-navy underline"
          >
            {t('stores.showAllAreas')}
          </button>
        )}
      </div>
    </div>
  )
}
